import { Link } from "@tanstack/react-router";
import { ArrowUpRight, MapPin } from "lucide-react";
import { RANGPUR_AREAS } from "@/content/rangpur-areas";

type Props = {
  eyebrow?: string;
  title?: React.ReactNode;
  description?: string;
  className?: string;
};

/**
 * Compact internal-link strip surfacing every Rangpur hyperlocal area page.
 * Drop into Rangpur-relevant service/landing pages to give users and
 * crawlers a direct path to neighborhood-level SEO content.
 */
export function RangpurAreasStrip({
  eyebrow = "Hyperlocal · Rangpur",
  title = (
    <>
      Serving every <span className="text-gradient">corner of Rangpur.</span>
    </>
  ),
  description = "Dedicated landing pages for the bazaars, intersections, campuses, and corridors buyers search by name — from Medical Mor and Shapla Chattar to Begum Rokeya University, Tajhat, and Haragash.",
  className,
}: Props) {
  return (
    <section className={`container-x py-16 ${className ?? ""}`}>
      <div className="rounded-3xl border border-border bg-card/60 p-8 md:p-10">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary/80">
          <MapPin size={12} /> {eyebrow}
        </div>
        <h2 className="mt-3 font-display text-3xl md:text-4xl leading-tight">{title}</h2>
        <p className="mt-3 max-w-2xl text-sm md:text-base text-muted-foreground">{description}</p>

        <ul className="mt-7 flex flex-wrap gap-2">
          {RANGPUR_AREAS.map((a) => (
            <li key={a.slug}>
              <Link
                to="/seo-services/rangpur/$area"
                params={{ area: a.slug }}
                className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-1.5 text-xs md:text-sm text-muted-foreground hover:border-primary hover:text-foreground transition"
              >
                {a.name}
                <ArrowUpRight size={12} className="opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all text-primary" />
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to="/seo-services/$location"
            params={{ location: "rangpur" }}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90"
          >
            Rangpur SEO hub <ArrowUpRight size={14} />
          </Link>
          <Link
            to="/seo-locations"
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-medium hover:bg-card"
          >
            All locations
          </Link>
        </div>
      </div>
    </section>
  );
}